<template>
	<StandardModal
		class="select-scopes-modal"
		open
		:title="$t('settings_apitokens_select_scopes_title')"
		@cancel="$emit('cancel')"
		:loading="retrieving_scopes"
	>
		<div class="select-scopes">
			<header class="help">
				{{ $t('settings_apitokens_select_scopes_help') }}
				<a :href="$t('common_apitoken_learnmore_url')" target="_blank">
					{{ $t('common_learn_more') }}
				</a>
			</header>
			<div class="list" :class="{ failed: failed_retrieving }">
				<div v-if="failed_retrieving" class="failed-retrieving">
					<ExclaimBackgroundIcon />
					<div class="unable-message">
						{{ $t('settings_apitokens_error_retrieve_scopes') }}.
					</div>
					<div class="try-again" @click="getScopes">
						{{ $t('chat_message_senderror_option_retry') }}
					</div>
				</div>
				<label
					:for="scope.scope"
					v-for="scope in scopes"
					:key="scope.scope"
					class="scope"
					@click="scope.checked = !scope.checked"
				>
					<Checkbox :id="scope.scope" v-model="scope.checked" />
					<div class="scope-label">{{ scope.scope }}</div>
					<div class="scope-description">
						{{ scope.description }}
					</div>
				</label>
			</div>
		</div>
		<template #footer-left>
			<Button @click="$emit('back')">
				{{ $t('common_button_back') }}
			</Button>
		</template>
		<template #footer-right>
			<Button
				primary
				:disabled="disabled"
				:loading="creating"
				@click="createToken"
			>
				{{ $t('common_button_create') }}
			</Button>
		</template>
	</StandardModal>
</template>

<script>
import { createToken, getScopes } from './apitokens'
import { ExclaimBackgroundIcon } from '@/icons'
import { StandardModal, Button, Checkbox } from '@/components/common'

export default {
	name: 'SelectScopes',
	components: { ExclaimBackgroundIcon, StandardModal, Button, Checkbox },
	props: ['name'],
	data() {
		return {
			creating: false,
			failed_retrieving: false,
			retrieving_scopes: false,
			scopes: [],
		}
	},

	emits: ['cancel', 'back', 'next'],

	created() {
		this.getScopes()
	},

	computed: {
		disabled() {
			return this.selectedScopes.length === 0
		},

		selectedScopes() {
			return this.scopes.filter((s) => s.checked).map((s) => s.scope)
		},
	},

	methods: {
		async getScopes() {
			this.failed_retrieving = false
			this.retrieving_scopes = true
			const response = await getScopes()
			if (!response.ok) {
				this.failed_retrieving = true
			} else {
				this.scopes = response.scopes
			}
			this.retrieving_scopes = false
		},
		async createToken() {
			const { name, selectedScopes } = this

			this.creating = true

			try {
				const { ok, token } = await createToken({
					name,
					scopes: selectedScopes,
				})

				if (!ok) {
					this.$alert.error(this.$t('settings_apitokens_error_create_token'))
				} else {
					this.$emit('next', token)
				}
			} catch (e) {
				console.error(e)
				this.$alert.error()
			}

			this.creating = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

header {
	text-align: center;
	padding: 24px 24px 16px 24px;
}

.title {
	font-family: 'Avenir-Black';
	font-size: 24px;
	color: var(--neutral-800);
	margin-bottom: 12px;
}

.help {
	font-family: 'Avenir-Medium';
	font-size: 16px;
	color: var(--neutral-600);
	text-align: center;
}

.select-scopes {
	height: 100%;
	display: flex;
	align-items: stretch;
	flex-direction: column;
}

.list {
	flex-grow: 1;
	overflow-y: auto;
	padding: 0 8px;
	@include flex-column;

	&.failed {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: row;
	}

	.failed-retrieving {
		flex-grow: 1;
		font-family: Avenir-Medium;
		font-size: 16px;
		color: var(--neutral-600);
		text-align: center;

		.unable-message {
			margin-top: 25px;
			margin-bottom: 10px;
		}

		.try-again {
			color: #1083c7;
			font-family: 'Avenir-Heavy';
			cursor: pointer;
		}
	}
}

.scope {
	@include flex(12px);
	width: 100%;
	cursor: pointer;
	align-items: center;
	border-bottom: 1px solid var(--neutral-200);
	height: 50px;
	flex-shrink: 0;
	font-size: 14px;

	.pronto-checkbox {
		width: 20px;
		font-size: 18px;
	}
	.scope-label {
		user-select: none;
		flex-shrink: 0;
		width: 160px;
	}
	.scope-description {
		flex-grow: 1;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.select-scopes-modal {
	.modal-body {
		display: flex;
		flex-direction: column;
		align-items: stretch;
	}
}

.select-scopes .help a,
.select-scopes .help a:visited {
	font-family: 'Avenir-Heavy';
	color: var(--blue-300);
	text-decoration: none;
}
</style>
